"use client"

import { useState, useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { FormSectionCard } from "./FormSectionCard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  PlusCircle,
  Upload,
  Trash2,
  Files,
} from "lucide-react"
import { useLanguage } from "@/contexts/language-context"
import { useFormSectionNavigation } from "@/hooks/use-form-section-navigation"
import { useDocuments, type DocumentItem } from "@/hooks/use-documents"
import { documentUploadSchema, type DocumentUploadFormData } from "@/lib/schemas"
import { useFormatters } from "@/hooks/use-formatters"
import React from "react"

// Simplified display format for documents
interface DocumentDisplay {
  id: number
  fileName: string
  customName: string
  size: string
  uploadedBy: string
  dateUploaded: Date
}

interface DocumentsSectionProps {
  isCompleted: boolean
  recordId?: number // The engagement record ID
  onCompletionChange?: (isCompleted: boolean) => void // Callback to notify parent of completion status
  isReadonly?: boolean
  nextButtonOverride?: React.ReactNode
  hiddenSections: Record<string, boolean>
}

export const DocumentsSection = function DocumentsSection({
  isCompleted,
  recordId,
  onCompletionChange,
  nextButtonOverride,
  isReadonly = false,
  hiddenSections,
}: DocumentsSectionProps) {
  const { dictionary } = useLanguage()
  const navigationProps = useFormSectionNavigation('documents', hiddenSections)
  const { formatDate, formatTimestamp } = useFormatters()
  const { isUploading, isFetching, uploadDocument: uploadDocumentApi, removeDocument: removeDocumentApi, viewDocument, downloadDocument, formatFileSize, fetchDocuments } = useDocuments({ recordId, dictionary })
  const [isUploadingDocument, setIsUploadingDocument] = useState(false)
  const [documents, setDocuments] = useState<DocumentItem[]>([])
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const documentUploadFormResolver = useMemo(() => zodResolver(documentUploadSchema), [])

  // Form for document upload dialog
  const uploadForm = useForm<DocumentUploadFormData>({
    resolver: documentUploadFormResolver,
    mode: 'onChange',
    defaultValues: {
      documentName: '',
      file: undefined as any // Will be set when file is selected
    }
  })

  // Transform API document to display format
  const transformApiDocument = (apiDoc: DocumentItem): DocumentDisplay => ({
    id: apiDoc.Id,
    fileName: apiDoc.Name, // The actual file name (e.g., "specs.docx")
    customName: apiDoc.Description || "-", // User-entered custom name or dash if none provided
    size: formatFileSize(apiDoc.Size),
    uploadedBy: apiDoc.UploadedByFullName,
    dateUploaded: new Date(apiDoc.UploadedDatetime),
  })

  // Load documents when component mounts or recordId changes
  useEffect(() => {
    if (!recordId) return
    
    setHasInitiallyLoaded(false) // Reset loading state when recordId changes
    
    // Call fetchDocuments directly to get the latest reference
    fetchDocuments().then(docs => {
      setDocuments(docs)
      setHasInitiallyLoaded(true)
    })
  }, [recordId]) // Only depend on recordId, not the function reference

  // Transform documents for display
  const displayDocuments = documents.map(transformApiDocument)

  // Calculate completion status and notify parent when it changes
  useEffect(() => {
    const isDocumentsComplete = documents.length > 0
    onCompletionChange?.(isDocumentsComplete)
  }, [documents.length, onCompletionChange])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      uploadForm.setValue('file', file)
      
      // Auto-fill document name if it's empty
      const currentDocumentName = uploadForm.getValues('documentName')
      if (!currentDocumentName) {
        uploadForm.setValue('documentName', file.name.split(".")[0])
      }
      
      // Trigger validation, since one to two fields may have changed
      uploadForm.trigger()
    }
  }

  const handleFileSelect = (file: File) => {
    uploadForm.setValue('file', file)
    
    // Auto-fill document name if it's empty
    const currentDocumentName = uploadForm.getValues('documentName')
    if (!currentDocumentName) {
      uploadForm.setValue('documentName', file.name.split(".")[0])
    }
    
    // Trigger validation, since one to two fields may have changed
    uploadForm.trigger()
  }

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    
    const files = e.dataTransfer.files
    if (files && files[0]) {
      handleFileSelect(files[0])
    }
  }

  const uploadDocument = uploadForm.handleSubmit(async (data) => {
    if (!data.file || !data.documentName) return

    const success = await uploadDocumentApi(data.file, data.documentName)
    
    if (success) {
      // Reset form state and close dialog immediately for better UX
      setIsUploadingDocument(false)
      uploadForm.reset({
        documentName: '',
        file: undefined as any
      })

      // Refresh the document list from API in the background
      const docs = await fetchDocuments()
      setDocuments(docs)
    } else {
      // On failure, keep everything as is so user can retry
      // The useDocuments hook already shows error toast
    }
  })

  const removeDocument = async (id: number) => {
    // Find the document to get its PublicId
    const document = documents.find(doc => doc.Id === id)
    if (!document) {
      console.error('Document not found with ID:', id)
      return
    }

    const success = await removeDocumentApi(document.PublicId)
    
    if (success) {
      // Refresh the document list from API in the background
      const docs = await fetchDocuments()
      setDocuments(docs)
    }
    // If failed, the hook already shows error toast, no need to do anything else
  }

  return (
    <>
      <FormSectionCard
        id="documents"
        title={dictionary?.engagement?.documents?.title || "Documents"}
        description={dictionary?.engagement?.documents?.description || "Manage documents related to the engagement"}
        icon={Files}
        isCompleted={isCompleted}
        showPrevNext={true}
        {...navigationProps}
        nextButtonOverride={nextButtonOverride}
      >
        <div className="flex justify-end">
          <Dialog open={isUploadingDocument} onOpenChange={(open) => {
            setIsUploadingDocument(open)
            if (open) {
              // Trigger validation when dialog opens to show required field errors
              uploadForm.trigger()
            } else {
              // Reset form when modal is closed
              uploadForm.reset({
                documentName: '',
                file: undefined as any
              })
              uploadForm.trigger()
              // Reset the file input
              const fileInput = document.getElementById("document-upload") as HTMLInputElement
              if (fileInput) {
                fileInput.value = ""
              }
            }
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1" disabled={isReadonly}>
                <PlusCircle className="h-4 w-4" />
                {dictionary?.engagement?.documents?.uploadDocument || "Upload Document"}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>
                  {dictionary?.engagement?.documents?.uploadDocument || "Upload Document"}
                </DialogTitle>
                <DialogDescription>
                  {dictionary?.engagement?.documents?.uploadDocumentDescription ||
                    "Upload a new document to the engagement"}
                </DialogDescription>
              </DialogHeader>
              <Form {...uploadForm}>
                <div className="space-y-4 py-4">
                  <FormField
                    control={uploadForm.control}
                    name="documentName"
                    render={({ field }) => (
                      <FormItem className="space-y-2.5">
                        <FormLabel className="text-sm font-medium">
                          {dictionary?.engagement?.documents?.documentName || "Document Name"}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={
                              dictionary?.engagement?.documents?.enterDocumentName || "Enter document name"
                            }
                            {...field}
                            className="form-field-focus"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={uploadForm.control}
                    name="file"
                    render={({ field }) => {
                      const selectedFile = field.value
                      return (
                        <FormItem className="grid w-full items-center gap-1.5">
                          <FormLabel className="text-sm font-medium">
                            {dictionary?.engagement?.documents?.uploadDocument || "Upload Document"}
                          </FormLabel>
                          <FormControl>
                            <div>
                              {!selectedFile ? (
                                <div className="flex items-center justify-center w-full">
                                  <label
                                    htmlFor="document-upload"
                                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-md cursor-pointer transition-colors ${
                                      isDragging 
                                        ? 'border-primary bg-primary/10' 
                                        : 'border-muted-foreground/25 bg-muted/10 hover:bg-muted/20'
                                    }`}
                                    onDragOver={handleDragOver}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                  >
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6 pointer-events-none">
                                      <Upload className="w-8 h-8 mb-2 text-muted-foreground" />
                                      <p className="mb-2 text-sm text-muted-foreground">
                                        <span className="font-semibold">
                                          {dictionary?.engagement?.documents?.clickToUpload || "Click to upload"}
                                        </span>{" "}
                                        {dictionary?.engagement?.documents?.orDragAndDrop || "or drag and drop"}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        PDF, DOCX, XLSX, PPTX, JPG, PNG (MAX. 10MB)
                                      </p>
                                    </div>
                                  </label>
                                </div>
                              ) : (
                                <div className="border-2 border-dashed border-primary/50 rounded-md p-4 bg-primary/5">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                                        <Files className="h-5 w-5 text-primary" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-sm">{selectedFile.name}</p>
                                        <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          field.onChange(null)
                                          // Reset the file input
                                          const fileInput = document.getElementById("document-upload") as HTMLInputElement
                                          if (fileInput) {
                                            fileInput.value = ""
                                          }
                                        }}
                                      >
                                        {dictionary?.engagement?.documents?.removeFile || "Remove File"}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Single file input used by both states */}
                              <Input
                                id="document-upload"
                                type="file"
                                className="hidden"
                                onChange={handleFileChange}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                </div>
              </Form>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsUploadingDocument(false)
                    uploadForm.reset({
                      documentName: '',
                      file: undefined as any
                    })
                  }}
                >
                  {dictionary?.engagement?.core?.cancel || "Cancel"}
                </Button>
                <Button onClick={uploadDocument} disabled={isUploading}>
                  {isUploading ? (dictionary?.engagement?.core?.uploading || "Uploading...") : (dictionary?.engagement?.documents?.upload || "Upload")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {(isFetching && !hasInitiallyLoaded) ? (
          <div className="border rounded-md p-12 text-center text-muted-foreground bg-muted/10 min-h-[300px] flex items-center justify-center">
            <div className="space-y-4">
              <p className="text-lg">Loading documents...</p>
            </div>
          </div>
        ) : displayDocuments.length > 0 ? (
          <div className="border rounded-md overflow-hidden shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="py-3 px-4 text-left font-medium text-sm">
                    {dictionary?.engagement?.documents?.file || "File"}
                  </th>
                  <th className="py-3 px-4 text-left font-medium text-sm">
                    {dictionary?.engagement?.documents?.documentName || "Document Name"}
                  </th>
                  <th className="py-3 px-4 text-left font-medium text-sm">
                    {dictionary?.engagement?.documents?.uploadedBy || "Uploaded By"}
                  </th>
                  <th className="py-3 px-4 text-left font-medium text-sm">
                    {dictionary?.engagement?.documents?.dateUploaded || "Date Uploaded"}
                  </th>
                  <th className="py-3 px-4 text-left font-medium text-sm">
                    {dictionary?.engagement?.core?.size || "Size"}
                  </th>
                  <th className="py-3 px-4 text-left font-medium text-sm">
                    {dictionary?.engagement?.core?.actions || "Actions"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayDocuments.map((doc) => (
                  <tr key={doc.id} className="border-b hover:bg-muted/10">
                    <td className="py-3 px-4">{doc.fileName}</td>
                    <td className="py-3 px-4">{doc.customName}</td>
                    <td className="py-3 px-4">{doc.uploadedBy}</td>
                    <td 
                      className="py-3 px-4" 
                      title={formatTimestamp(doc.dateUploaded)}
                    >
                      {formatDate(doc.dateUploaded)}
                    </td>
                    <td className="py-3 px-4">{doc.size}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="hover:bg-primary/10"
                          onClick={() => {
                            const document = documents.find(d => d.Id === doc.id)
                            if (document) {
                              viewDocument(document.PublicId)
                            }
                          }}
                        >
                          {dictionary?.engagement?.core?.view || "View"}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="hover:bg-primary/10"
                          onClick={() => {
                            const document = documents.find(d => d.Id === doc.id)
                            if (document) {
                              downloadDocument(document.PublicId)
                            }
                          }}
                        >
                          {dictionary?.engagement?.core?.download || "Download"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10"
                          disabled={isReadonly}
                          onClick={() => removeDocument(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">{dictionary?.engagement?.core?.delete || "Delete"}</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border rounded-md p-12 text-center text-muted-foreground bg-muted/10 min-h-[300px] flex items-center justify-center">
            <div className="space-y-4">
              <p className="text-lg">{dictionary?.engagement?.documents?.noDocumentsUploaded || "No documents have been uploaded yet."}</p>
              <p className="text-sm">Click the "Upload Document" button to add one.</p>
              <div className="text-xs text-muted-foreground/70 mt-6">
                Supported formats: PDF, DOCX, XLSX, JPG, PNG (MAX. 10MB)
              </div>
            </div>
          </div>
        )}
      </FormSectionCard>
    </>
  )
}

export default DocumentsSection